import { useMemo, useState } from 'react';
import {
  Warning,
  Briefcase,
  Exam,
  LinkedinLogo,
  Envelope,
  Microphone,
  Plus,
  MagnifyingGlass,
  UserList,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/layout/EmptyState';
import { useEndClients, type NeuraxEndClient } from '@/lib/end-clients/useEndClients';
import { AddEndClientDialog } from '@/components/end-clients/AddEndClientDialog';
import { ScheduleSessionWizard } from '@/components/sessions/ScheduleSessionWizard';
import type { SessionType } from '@/lib/sessions/useScheduleSession';

export default function EndClientsPage() {
  const { data, isLoading, isError, error } = useEndClients();
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [scheduleFor, setScheduleFor] = useState<{ id: string; type: SessionType } | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter((c) =>
      [c.name, c.email, c.target_company, c.job_title]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q))
    );
  }, [data, query]);

  return (
    <>
      <PageHeader
        eyebrow="Candidates"
        title="End clients"
        description="Add the candidates who will receive interview prep and NeuraX licenses."
        actions={
          <button
            data-testid="add-end-client-btn"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-neurax-electric to-neurax-plasma px-4 py-2.5 text-sm font-semibold text-white shadow-glow-soft transition hover:brightness-110"
          >
            <Plus className="h-4 w-4" />
            Add candidate
          </button>
        }
      />

      <div className="mb-5 flex items-center gap-3">
        <div className="relative w-full max-w-md">
          <MagnifyingGlass className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="end-clients-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, or target company"
            className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-muted-foreground/60 outline-none transition focus:border-neurax-electric/60 focus:ring-2 focus:ring-neurax-electric/30"
          />
        </div>
      </div>

      {isError && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <Warning className="mt-0.5 h-4 w-4" />
          <span>{(error as Error)?.message || 'Failed to load candidates.'}</span>
        </div>
      )}

      {isLoading && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              data-testid="end-client-skeleton"
              className="glass-card h-32 animate-pulse"
            />
          ))}
        </div>
      )}

      {!isLoading && filtered.length === 0 && (
        <div data-testid="end-clients-empty">
          <EmptyState
            title={query ? 'No candidates match your search' : 'No candidates yet'}
            description={
              query
                ? 'Try a different name, email, or company.'
                : 'Add your first candidate to start issuing NeuraX licenses.'
            }
          />
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((c) => (
            <CandidateCard
              key={c.id}
              c={c}
              onScheduleInterview={() => setScheduleFor({ id: c.id, type: 'interview' })}
              onScheduleTest={() => setScheduleFor({ id: c.id, type: 'online_assessment' })}
            />
          ))}
        </div>
      )}

      {addOpen && <AddEndClientDialog open onClose={() => setAddOpen(false)} />}

      {scheduleFor && (
        <ScheduleSessionWizard
          open
          onClose={() => setScheduleFor(null)}
          sessionType={scheduleFor.type}
          defaultEndClientId={scheduleFor.id}
        />
      )}
    </>
  );
}

function CandidateCard({
  c,
  onScheduleInterview,
  onScheduleTest,
}: {
  c: NeuraxEndClient;
  onScheduleInterview: () => void;
  onScheduleTest: () => void;
}) {
  const initials = c.name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div
      data-testid={`end-client-row-${c.id}`}
      className="glass-card group p-5 transition hover:border-neurax-electric/30"
    >
      <div className="flex items-start gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-neurax-electric/40 to-neurax-plasma/40 text-sm font-semibold text-white">
          {initials || <UserList className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-white">{c.name}</p>
          <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
            <Envelope className="h-3 w-3" />
            {c.email}
          </p>
        </div>
        {c.linkedin_url && (
          <a
            href={c.linkedin_url}
            target="_blank"
            rel="noreferrer"
            className="opacity-0 transition group-hover:opacity-100 text-muted-foreground hover:text-neurax-electric"
            aria-label="LinkedIn profile"
          >
            <LinkedinLogo className="h-4 w-4" />
          </a>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Role</p>
          <p className="mt-0.5 truncate text-white">{c.job_title || '—'}</p>
        </div>
        <div className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
          <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            <Briefcase className="h-3 w-3" />
            Target
          </p>
          <p className="mt-0.5 truncate text-white">{c.target_company || '—'}</p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={onScheduleInterview}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-medium text-white transition hover:border-neurax-electric/40 hover:bg-neurax-electric/[0.08]"
        >
          <Microphone className="h-3.5 w-3.5 text-neurax-electric" />
          Interview
        </button>
        <button
          onClick={onScheduleTest}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-medium text-white transition hover:border-neurax-plasma/40 hover:bg-neurax-plasma/[0.08]"
        >
          <Exam className="h-3.5 w-3.5 text-neurax-plasma" />
          Test
        </button>
      </div>
    </div>
  );
}
